// frontend/pages/Settings.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '../services/api';  // Changed from settingsAPI to settingsService
import { useAuth } from '../hooks/useAuth';  // Added missing import
import UserManagement from '../components/Settings/UserManagement';
import TaxSettings from '../components/Settings/TaxSettings';
import SMSSettings from '../components/Settings/SMSSettings';
import PrintSettings from '../components/Settings/PrintSettings';
import ProfileSettings from '../components/Settings/ProfileSettings';
import MpesaSettings from '../components/Settings/MpesaSettings';
import { toast } from 'react-hot-toast';

const settingsTabs = [
  { id: 'profile', name: 'Profile', component: ProfileSettings },
  { id: 'mpesa', name: 'M-Pesa', component: MpesaSettings, permission: 'manage_settings' },
  { id: 'users', name: 'User Management', component: UserManagement, permission: 'manage_users' },
  { id: 'tax', name: 'Tax Settings', component: TaxSettings, permission: 'manage_tax_config' },
  { id: 'sms', name: 'SMS Settings', component: SMSSettings },
  { id: 'printing', name: 'Print Settings', component: PrintSettings }
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const { user } = useAuth();  // Now this will work
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsService.getSettings()  // Changed from settingsAPI to settingsService
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (data) => settingsService.updateSettings(data),  // Changed from settingsAPI to settingsService
    onSuccess: () => {
      toast.success('Settings updated successfully');
      queryClient.invalidateQueries(['settings']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    }
  });

  const filteredTabs = settingsTabs.filter(tab => {
    if (!tab.permission) return true;
    return user?.role === 'admin' || user?.role === 'superadmin';
  });

  const ActiveComponent = filteredTabs.find(tab => tab.id === activeTab)?.component;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600">Manage your system preferences and configurations</p>
      </div>

      {/* Settings Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8 overflow-x-auto">
          {filteredTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Active Settings Component */}
      <div className="bg-white rounded-lg shadow p-6">
        {ActiveComponent ? (
          <ActiveComponent 
            settings={settings} 
            onUpdate={updateSettingsMutation.mutate}
            loading={updateSettingsMutation.isPending}
          />
        ) : (
          <div className="text-center text-gray-500 py-8">
            Select a settings tab to configure
          </div>
        )}
      </div>
    </div>
  );
}